'use client';

import { Swiper, SwiperSlide } from 'swiper/react';

import { CompanyPageImages } from '../markupConfig';

import { AppImageClient } from '@/shared/components/appImage';
import { useIsMobile } from '@/shared/hooks';

interface NavigationTabContentProps {
  pageTranslations: Record<string, string>;
  images: CompanyPageImages;
  /** Подписанный URL анимированного gif с телефоном (не через AppImage). */
  phoneAnimationSrc: string;
}

type SlideProps = Pick<
  NavigationTabContentProps,
  'pageTranslations' | 'images'
>;

const RoadGraphSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px max-lg:bg-surface-base-secondary relative h-full pt-[152px] pr-5 pb-2 pl-8 max-lg:min-h-[368px] max-lg:overflow-hidden max-lg:rounded-[20px] max-lg:px-5">
    <AppImageClient
      {...images.navigationTab1}
      alt="bg"
      className="absolute top-0 left-0 h-full w-full object-cover max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesRoadGraphTitle"
      className="text-text-base-primary text-[18px] leading-[24px] max-lg:text-[16px] max-lg:leading-[20px]"
    >
      {pageTranslations.detalizationNavigationSectionFeaturesRoadGraphTitle}
    </p>
    <p
      id="detalizationNavigationSectionFeaturesRoadGraphDescription"
      className="text-text-base-primary typo-text-m mt-2 pb-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesRoadGraphDescription
      }
    </p>
    <div className="bg-surface-base-primary absolute top-0 right-[-1px] h-full w-[2px] max-lg:hidden"></div>
  </div>
);

const TransportGraphSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px max-lg:bg-surface-base-secondary relative h-full pt-[152px] pr-5 pl-8 max-lg:min-h-[368px] max-lg:overflow-hidden max-lg:rounded-[20px] max-lg:px-5">
    <AppImageClient
      {...images.navigationTab2}
      alt="bg"
      className="absolute top-0 left-0 h-full w-full object-cover max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesTransportGraphTitle"
      className="text-text-base-primary text-[18px] leading-[24px] max-lg:text-[16px] max-lg:leading-[20px]"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesTransportGraphTitle
      }
    </p>
    <p
      id="detalizationNavigationSectionFeaturesTransportGraphDescription"
      className="text-text-base-primary typo-text-m mt-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesTransportGraphDescription
      }
    </p>
    <div className="bg-surface-base-primary absolute top-0 right-[-1px] h-full w-[2px]"></div>
  </div>
);

const PedestrianGraphSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px max-lg:bg-surface-base-secondary relative h-full pt-[152px] pr-5 pl-8 max-lg:min-h-[308px] max-lg:overflow-hidden max-lg:rounded-[20px] max-lg:px-5">
    <AppImageClient
      {...images.navigationTab3}
      alt="bg"
      className="absolute top-0 left-0 h-full w-full object-cover max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesPedestrianGraphTitle"
      className="text-text-base-primary text-[18px] leading-[24px] max-lg:text-[16px] max-lg:leading-[20px]"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesPedestrianGraphTitle
      }
    </p>
    <p
      id="detalizationNavigationSectionFeaturesPedestrianGraphDescription"
      className="text-text-base-primary typo-text-m mt-2 max-lg:pb-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesPedestrianGraphDescription
      }
    </p>
  </div>
);

const RouteTypesSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px bg-surface-base-secondary max-lg:bg-surface-base-secondary relative h-full min-h-[472px] w-full overflow-hidden rounded-[20px] px-5 pt-[342px] pb-[10px] max-lg:min-h-[378px] max-lg:overflow-hidden max-lg:rounded-[20px]">
    <AppImageClient
      {...images.navigationTab4}
      alt="bg"
      className="absolute top-0 left-0 h-full w-full object-cover max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesRouteTypesDescription"
      className="text-text-base-primary typo-text-m relative z-[1] pb-2 max-lg:pb-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesRouteTypesDescription
      }
    </p>
  </div>
);

const BuildingEntriesSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px bg-surface-base-secondary max-lg:bg-surface-base-secondary relative h-full min-h-[472px] w-full rounded-[20px] p-2 max-lg:min-h-[357px] max-lg:overflow-hidden max-lg:rounded-[20px]">
    <AppImageClient
      {...images.navigationTab5}
      alt="bg"
      className="h-auto w-full rounded-[12px] max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesBuildingEntriesDescription"
      className="text-text-base-primary typo-text-m relative z-[1] mt-6 px-3 max-lg:pb-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesBuildingEntriesDescription
      }
    </p>
  </div>
);

const IndoorRoutesSlide = ({
  pageTranslations,
  images,
  phoneAnimationSrc,
}: SlideProps & Pick<NavigationTabContentProps, 'phoneAnimationSrc'>) => (
  <div className="bg-surface-base-secondary max-lg:bg-surface-base-secondary relative col-span-2 flex h-full min-h-[386px] w-full justify-end rounded-[20px] pt-12 pr-5 max-lg:min-h-[522px] max-lg:overflow-hidden max-lg:rounded-[20px] max-lg:pt-[320px]">
    <AppImageClient
      {...images.navigationTabGreenBg}
      alt="bg"
      className="absolute bottom-0 left-0 h-full w-auto max-w-none max-lg:hidden"
    />
    <AppImageClient
      {...images.navigationTabPhone}
      alt="bg"
      className="absolute bottom-0 left-[173px] h-auto w-[308px] max-lg:top-0 max-lg:left-1/2 max-lg:w-[220px] max-lg:translate-x-[-50%]"
    />
    <img
      src={phoneAnimationSrc}
      alt="bg"
      className="absolute bottom-0 left-[200px] w-[255px] max-lg:top-[40px] max-lg:left-1/2 max-lg:w-[186px] max-lg:translate-x-[-50%]"
    />
    <AppImageClient
      {...images.navigationTabPhoneOverlay}
      alt="bg"
      className="absolute bottom-0 left-[106px] h-auto w-[454px] max-lg:top-[210px] max-lg:left-1/2 max-lg:w-[320px] max-lg:translate-x-[-50%]"
    />
    <div className="max-w-[520px] max-lg:max-w-full max-lg:px-5">
      <p
        id="detalizationNavigationSectionFeaturesIndoorRoutesTitle"
        className="text-text-base-primary relative text-[24px] leading-[32px] max-lg:text-[24px] max-lg:leading-[28px]"
      >
        {
          pageTranslations.detalizationNavigationSectionFeaturesIndoorRoutesTitle
        }
      </p>
      <p
        id="detalizationNavigationSectionFeaturesIndoorRoutesDescription"
        className="text-text-base-primary typo-text-m mt-4"
      >
        {
          pageTranslations.detalizationNavigationSectionFeaturesIndoorRoutesDescription
        }
      </p>
    </div>
  </div>
);

const TrafficUpdatesSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="bg-surface-base-secondary max-lg:bg-surface-base-secondary relative h-full min-h-[448px] w-full rounded-[20px] px-5 pt-[344px] pb-[10px] max-lg:min-h-[297px] max-lg:overflow-hidden max-lg:rounded-[20px] max-lg:pt-[190px]">
    <AppImageClient
      {...images.navigationTab6}
      alt="bg"
      className="absolute top-0 left-0 h-full w-full object-cover max-lg:h-auto max-lg:w-full"
    />
    <p
      id="detalizationNavigationSectionFeaturesTrafficUpdatesDescription"
      className="text-text-base-primary typo-text-m relative z-[1] pb-2 max-lg:pb-2"
    >
      {
        pageTranslations.detalizationNavigationSectionFeaturesTrafficUpdatesDescription
      }
    </p>
  </div>
);

const EtaSlide = ({ pageTranslations, images }: SlideProps) => (
  <div className="max-lg:pt-172px bg-surface-base-secondary max-lg:bg-surface-base-secondary relative h-full min-h-[448px] w-full rounded-[20px] p-2 max-lg:min-h-[297px] max-lg:overflow-hidden max-lg:rounded-[20px]">
    <AppImageClient
      {...images.navigationTab7}
      alt="bg"
      className="h-auto w-full rounded-[12px] max-lg:h-auto max-lg:w-full"
    />
    <p
      id="etaDescription"
      className="text-text-base-primary typo-text-m relative z-[1] mt-6 px-3 max-lg:pb-2"
    >
      {pageTranslations.etaDescription}{' '}
      <span className="border-surface-base-blur bg-custom-surface-contrast-low mt-[3px] inline-block min-h-[26px] items-center rounded-[7px] border px-2">
        ETA/RTA
      </span>
    </p>
  </div>
);

export const NavigationTabContent: React.FC<NavigationTabContentProps> = ({
  pageTranslations,
  images,
  phoneAnimationSrc,
}) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Swiper spaceBetween={12} slidesPerView="auto" className="!px-5 !py-2">
        <SwiperSlide>
          <RoadGraphSlide pageTranslations={pageTranslations} images={images} />
        </SwiperSlide>
        <SwiperSlide>
          <TransportGraphSlide
            pageTranslations={pageTranslations}
            images={images}
          />
        </SwiperSlide>
        <SwiperSlide>
          <PedestrianGraphSlide
            pageTranslations={pageTranslations}
            images={images}
          />
        </SwiperSlide>
        <SwiperSlide>
          <RouteTypesSlide
            pageTranslations={pageTranslations}
            images={images}
          />
        </SwiperSlide>
        <SwiperSlide>
          <BuildingEntriesSlide
            pageTranslations={pageTranslations}
            images={images}
          />
        </SwiperSlide>
        <SwiperSlide>
          <IndoorRoutesSlide
            pageTranslations={pageTranslations}
            images={images}
            phoneAnimationSrc={phoneAnimationSrc}
          />
        </SwiperSlide>
        <SwiperSlide>
          <TrafficUpdatesSlide
            pageTranslations={pageTranslations}
            images={images}
          />
        </SwiperSlide>
        <SwiperSlide>
          <EtaSlide pageTranslations={pageTranslations} images={images} />
        </SwiperSlide>
      </Swiper>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-[32px] max-lg:flex max-lg:flex-col">
      <div className="bg-surface-base-secondary col-span-2 grid min-h-[389px] w-full grid-cols-3 rounded-[20px] max-lg:flex max-lg:h-auto max-lg:w-full max-lg:flex-col max-lg:gap-[20px] max-lg:rounded-[20px] max-lg:bg-transparent max-lg:p-1">
        <RoadGraphSlide pageTranslations={pageTranslations} images={images} />

        <TransportGraphSlide
          pageTranslations={pageTranslations}
          images={images}
        />

        <PedestrianGraphSlide
          pageTranslations={pageTranslations}
          images={images}
        />
      </div>

      <RouteTypesSlide pageTranslations={pageTranslations} images={images} />

      <BuildingEntriesSlide
        pageTranslations={pageTranslations}
        images={images}
      />

      <IndoorRoutesSlide
        pageTranslations={pageTranslations}
        images={images}
        phoneAnimationSrc={phoneAnimationSrc}
      />

      <TrafficUpdatesSlide
        pageTranslations={pageTranslations}
        images={images}
      />

      <EtaSlide pageTranslations={pageTranslations} images={images} />
    </div>
  );
};
